import React, { useEffect, useState } from 'react';
import {
    Send, Search, Plus, Calendar, CheckCircle, XCircle,
    Clock, FolderKanban, Wifi, ChevronLeft, ChevronRight
} from 'lucide-react';
import { usePublishingStore } from '../../store/publishingStore';
import { ScheduledPost, Campaign, PlatformConnection } from '../../services/publishing.service';
import { toast } from 'sonner';
import PostCard from './components/PostCard';
import PlatformConnectionCard, {
    UnconnectedPlatformCard,
    AVAILABLE_PLATFORMS,
    PlatformConfig,
} from './components/PlatformConnectionCard';
import SchedulePostModal from './SchedulePostModal';
import CampaignModal from './CampaignModal';
import ConnectPlatformModal from './ConnectPlatformModal';

const PublishingPage: React.FC = () => {
    const {
        posts,
        campaigns,
        platforms,
        stats,
        isLoading,
        activeTab,
        filters,
        searchTerm,
        fetchPosts,
        fetchCampaigns,
        fetchPlatforms,
        fetchStats,
        setActiveTab,
        setFilters,
        setSearchTerm,
        getFilteredPosts,
        publishNow,
        updatePost,
        deleteCampaign,
        refreshPlatform,
        disconnectPlatform,
    } = usePublishingStore();

    // Modal states
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
    const [isConnectPlatformModalOpen, setIsConnectPlatformModalOpen] = useState(false);
    const [editingPost, setEditingPost] = useState<ScheduledPost | null>(null);
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [connectingPlatform, setConnectingPlatform] = useState<PlatformConfig | null>(null);

    // Pagination
    const ITEMS_PER_PAGE = 10;
    const [postPage, setPostPage] = useState(1);
    const [campaignPage, setCampaignPage] = useState(1);

    useEffect(() => {
        fetchPosts();
        fetchCampaigns();
        fetchPlatforms();
        fetchStats();
    }, [fetchPosts, fetchCampaigns, fetchPlatforms, fetchStats]);

    // Reset page when filters change
    useEffect(() => {
        setPostPage(1);
    }, [filters, searchTerm]);

    const filteredPosts = getFilteredPosts();
    const paginatedPosts = filteredPosts.slice((postPage - 1) * ITEMS_PER_PAGE, postPage * ITEMS_PER_PAGE);
    const paginatedCampaigns = campaigns.slice((campaignPage - 1) * ITEMS_PER_PAGE, campaignPage * ITEMS_PER_PAGE);
    const totalPostPages = Math.ceil(filteredPosts.length / ITEMS_PER_PAGE);
    const totalCampaignPages = Math.ceil(campaigns.length / ITEMS_PER_PAGE);

    // Get unconnected platforms
    const connectedPlatformTypes = platforms.map(p => p.platform);
    const unconnectedPlatforms = AVAILABLE_PLATFORMS.filter(p => !connectedPlatformTypes.includes(p.platform));

    // Handlers
    const handleEditPost = (post: ScheduledPost) => {
        setEditingPost(post);
        setIsScheduleModalOpen(true);
    };

    const handleCancelPost = async (post: ScheduledPost) => {
        if (window.confirm('Are you sure you want to cancel this scheduled post?')) {
            await updatePost(post.id, { status: 'cancelled' });
        }
    };

    const handlePublishNow = async (post: ScheduledPost) => {
        if (window.confirm('Publish this post now?')) {
            await publishNow(post.id);
        }
    };

    const handleViewAnalytics = (_post: ScheduledPost) => {
        toast.info('Analytics view coming soon!');
    };

    const handleEditCampaign = (campaign: Campaign) => {
        setEditingCampaign(campaign);
        setIsCampaignModalOpen(true);
    };

    const handleDeleteCampaign = async (campaign: Campaign) => {
        if (window.confirm(`Delete campaign "${campaign.name}"?`)) {
            await deleteCampaign(campaign.id);
        }
    };

    const handleRefreshPlatform = async (platform: PlatformConnection) => {
        await refreshPlatform(platform.id);
    };

    const handleDisconnectPlatform = async (platform: PlatformConnection) => {
        if (window.confirm(`Disconnect ${platform.platform}?`)) {
            await disconnectPlatform(platform.id);
        }
    };

    const handleConnectPlatform = (config: PlatformConfig) => {
        setConnectingPlatform(config);
        setIsConnectPlatformModalOpen(true);
    };

    // Status options
    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'ready', label: 'Ready' },
        { value: 'needs_media', label: 'Needs Media' },
        { value: 'published', label: 'Published' },
        { value: 'failed', label: 'Failed' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    // Pagination component
    const PaginationControls = ({
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
    }: {
        currentPage: number;
        totalPages: number;
        onPageChange: (page: number) => void;
        totalItems: number;
    }) => {
        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages} ({totalItems} items)
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>
            </div>
        );
    };

    if (isLoading && posts.length === 0 && campaigns.length === 0) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Publishing & Distribution</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Manage, schedule, and distribute content across platforms</p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Scheduled</div>
                        <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-blue-500" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white">{stats?.scheduled || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Published</div>
                        <div className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-xl font-bold text-green-600">{stats?.published || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                        <div className="flex items-center gap-1">
                            <XCircle className="w-4 h-4 text-red-500" />
                            <span className="text-xl font-bold text-red-600">{stats?.failed || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Campaigns</div>
                        <div className="flex items-center gap-1">
                            <FolderKanban className="w-4 h-4 text-purple-500" />
                            <span className="text-xl font-bold text-purple-600">{campaigns.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Tabs */}
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                        <button
                            onClick={() => setActiveTab('queue')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'queue'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <Send className="w-4 h-4" />
                            Queue
                        </button>
                        <button
                            onClick={() => setActiveTab('campaigns')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'campaigns'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <FolderKanban className="w-4 h-4" />
                            Campaigns
                        </button>
                        <button
                            onClick={() => setActiveTab('platforms')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'platforms'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <Wifi className="w-4 h-4" />
                            Platforms
                            {platforms.filter(p => p.status === 'connected').length > 0 && (
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            )}
                        </button>
                    </div>

                    {/* Filters */}
                    {activeTab === 'queue' && (
                        <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl">
                            {/* Search */}
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search posts..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                                />
                            </div>

                            {/* Campaign Filter */}
                            <select
                                value={filters.campaignId}
                                onChange={(e) => setFilters({ campaignId: e.target.value })}
                                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors"
                            >
                                <option value="">All Campaigns</option>
                                {campaigns.map((campaign) => (
                                    <option key={campaign.id} value={campaign.id}>
                                        {campaign.name}
                                    </option>
                                ))}
                            </select>

                            {/* Status Filter */}
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ status: e.target.value })}
                                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors"
                            >
                                {statusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Action Button */}
                    <button
                        onClick={() => {
                            if (activeTab === 'queue') {
                                setEditingPost(null);
                                setIsScheduleModalOpen(true);
                            } else if (activeTab === 'campaigns') {
                                setEditingCampaign(null);
                                setIsCampaignModalOpen(true);
                            }
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        <Plus className="w-4 h-4" />
                        {activeTab === 'queue' ? 'Schedule Post' : activeTab === 'campaigns' ? 'New Campaign' : 'Connect Platform'}
                    </button>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'queue' && (
                <>
                    {filteredPosts.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/30 dark:to-indigo-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <Send className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No scheduled posts</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                Start scheduling your content across multiple platforms.
                            </p>
                            <button
                                onClick={() => {
                                    setEditingPost(null);
                                    setIsScheduleModalOpen(true);
                                }}
                                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                Schedule Your First Post
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {paginatedPosts.map((post) => (
                                    <PostCard
                                        key={post.id}
                                        post={post}
                                        onEdit={handleEditPost}
                                        onCancel={handleCancelPost}
                                        onPublishNow={handlePublishNow}
                                        onViewAnalytics={handleViewAnalytics}
                                    />
                                ))}
                            </div>
                            <PaginationControls
                                currentPage={postPage}
                                totalPages={totalPostPages}
                                onPageChange={setPostPage}
                                totalItems={filteredPosts.length}
                            />
                        </div>
                    )}
                </>
            )}

            {activeTab === 'campaigns' && (
                <>
                    {campaigns.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <FolderKanban className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No campaigns yet</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                Create campaigns to organize and track your content launches.
                            </p>
                            <button
                                onClick={() => {
                                    setEditingCampaign(null);
                                    setIsCampaignModalOpen(true);
                                }}
                                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                Create First Campaign
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {paginatedCampaigns.map((campaign) => (
                                    <div
                                        key={campaign.id}
                                        className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-5 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white">{campaign.name}</h3>
                                                {campaign.description && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                                        {campaign.description}
                                                    </p>
                                                )}
                                            </div>
                                            <span
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${campaign.status === 'active'
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                    : campaign.status === 'completed'
                                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                        : campaign.status === 'cancelled'
                                                            ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                    }`}
                                            >
                                                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {campaign.startDate
                                                    ? new Date(campaign.startDate).toLocaleDateString()
                                                    : 'No date'}
                                                {campaign.endDate && ` - ${new Date(campaign.endDate).toLocaleDateString()}`}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Send className="w-4 h-4" />
                                                {Array.isArray(campaign.scheduledPosts) ? campaign.scheduledPosts.length : 0} posts
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditCampaign(campaign)}
                                                className="flex-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors text-center"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCampaign(campaign)}
                                                className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <PaginationControls
                                currentPage={campaignPage}
                                totalPages={totalCampaignPages}
                                onPageChange={setCampaignPage}
                                totalItems={campaigns.length}
                            />
                        </div>
                    )}
                </>
            )}

            {activeTab === 'platforms' && (
                <div className="space-y-6">
                    {/* Connected Platforms */}
                    {platforms.length > 0 && (
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Connected Platforms</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {platforms.map((platform) => (
                                    <PlatformConnectionCard
                                        key={platform.id}
                                        platform={platform}
                                        onRefresh={handleRefreshPlatform}
                                        onDisconnect={handleDisconnectPlatform}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Available Platforms */}
                    {unconnectedPlatforms.length > 0 && (
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Platforms</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {unconnectedPlatforms.map((config) => (
                                    <UnconnectedPlatformCard
                                        key={config.id}
                                        config={config}
                                        onConnect={handleConnectPlatform}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            <SchedulePostModal
                isOpen={isScheduleModalOpen}
                onClose={() => {
                    setIsScheduleModalOpen(false);
                    setEditingPost(null);
                }}
                editingPost={editingPost}
                campaigns={campaigns}
                platforms={platforms}
            />

            <CampaignModal
                isOpen={isCampaignModalOpen}
                onClose={() => {
                    setIsCampaignModalOpen(false);
                    setEditingCampaign(null);
                }}
                editingCampaign={editingCampaign}
                platforms={platforms}
            />

            <ConnectPlatformModal
                isOpen={isConnectPlatformModalOpen}
                onClose={() => {
                    setIsConnectPlatformModalOpen(false);
                    setConnectingPlatform(null);
                }}
                platformConfig={connectingPlatform}
            />
        </div>
    );
};

export default PublishingPage;
